import {
  faBrakeWarning,
  faCaretRight,
  faCircleCheck,
  faFileCertificate,
  faLandmarkMagnifyingGlass,
  faSpinnerThird,
} from '@fortawesome/duotone-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu, MenuItem } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import Box from '~/common/components/Box';
import useCreditReportLinks from '~/common/components/Credit/useCreditReportLinks';
import Overflow from '~/common/components/Overflow';
import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '~/common/components/Tables';
import { useGetAUSReportLinks } from '~/common/hooks/useGetAUSReportLinks';
import useUrl from '~/common/hooks/useUrl';
import ToggleButtonGroup from '~/common/mui/Buttons/MUIButtonGroup/ToggleButtonGroup';
import { typedApiClient } from '~/common/util/api-client';
import palette from '~/theme/mui/palette';

import { isAUSStatusSuccess, openAUSReport } from './ausUtils';
import useAUSStore from './useAUSStore';

const iconStyle24: React.CSSProperties = {
  maxWidth: '24px',
  maxHeight: '24px',
  minWidth: '24px',
  minHeight: '24px',
};

const RecommendationCell = ({ text }: { text: string }) => {
  let icon, color;
  const textColor = text.includes('PROCESSED W/ ERRORS')
    ? palette.red[600]
    : palette.black;

  const recommendationIsAcceptible = isAUSStatusSuccess(text);

  if (recommendationIsAcceptible) {
    icon = faCircleCheck;
    color = palette.green[500];
  } else {
    icon = faBrakeWarning;
    color = palette.red[600];
  }
  return (
    <Box display="flex" alignItems="center" color={color}>
      <FontAwesomeIcon
        icon={icon}
        style={{
          marginRight: 8,
          ...iconStyle24,
        }}
      />
      <span style={{ color: textColor }}>{text}</span>
    </Box>
  );
};

const RanByCell = ({ username }: { username: string }) => (
  <Box
    sx={{
      display: 'inline-block',
      backgroundColor: 'gray.100',
      padding: '6px 8px',
      borderRadius: 4,
      fontSize: '0.75rem',
      maxWidth: '6.5rem',
    }}
  >
    <Overflow>{username}</Overflow>
  </Box>
);

const FindingsCell = ({
  type,
  requestId,
  requestDateTime,
  requestStatus,
}: {
  type: 'DU' | 'LPA';
  requestId: string;
  requestDateTime: string;
  requestStatus: string;
  recommendation: string;
}) => {
  const allowedStatuses = ['Processed Data'];
  const isCreditReportEnabled = allowedStatuses.includes(requestStatus);
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);

  const findingsQuery = useGetAUSReportLinks({
    requestId,
    ausMethod: type === 'DU' ? 1 : 2,
    requestDateTime,
    reportType: 'findings',
    requestStatus: type === 'LPA' ? isCreditReportEnabled : null,
    enabled: false,
  });
  const creditLinks = useCreditReportLinks(type, requestId, false);

  return (
    <Box sx={{ position: 'relative' }} ref={anchorRef}>
      <ToggleButtonGroup
        options={[
          {
            value: 'findings',
            label: (
              <>
                <FontAwesomeIcon
                  icon={
                    findingsQuery.isFetching
                      ? faSpinnerThird
                      : faLandmarkMagnifyingGlass
                  }
                  spin={findingsQuery.isFetching}
                  style={{
                    marginRight: 4,
                    ...iconStyle24,
                  }}
                />
                {type}
              </>
            ),
            onClick: () => {
              return void openAUSReport(
                findingsQuery.refetch,
                `${type} Findings`,
              );
            },
            disabled: findingsQuery.isError,
          },
          {
            value: 'certificate',
            label: (
              <>
                <FontAwesomeIcon
                  icon={
                    creditLinks.isFetching ? faSpinnerThird : faFileCertificate
                  }
                  spin={creditLinks.isFetching}
                  style={{
                    ...iconStyle24,
                  }}
                />
                {creditLinks.data?.creditReportLinks.length > 1 && (
                  <FontAwesomeIcon
                    icon={faCaretRight}
                    spin={creditLinks.isFetching}
                    style={{
                      ...iconStyle24,
                    }}
                  />
                )}
              </>
            ),
            sx: {
              width: '4.5rem',
            },
            onClick: () => {
              void (async () => {
                if (!creditLinks.data) {
                  try {
                    await creditLinks.refetch();
                  } catch {
                    return;
                  }
                }

                if (
                  creditLinks.isError ||
                  !creditLinks.data?.creditReportLinks.length
                ) {
                  return;
                }

                if (creditLinks.data.creditReportLinks.length > 1) {
                  setIsOpen(true);
                } else {
                  window.open(
                    creditLinks.data.creditReportLinks[0].creditReportUri,
                    '_blank',
                  );
                }
              })();
            },
            disabled: !isCreditReportEnabled || creditLinks.isError,
          },
        ]}
        size="medium"
      />
      <Menu
        open={isOpen && !!creditLinks.data?.creditReportLinks?.length}
        onClose={() => setIsOpen(false)}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {creditLinks.data?.creditReportLinks?.map((link) => (
          <MenuItem
            key={link.customerIdentifiers[0].customerRecordId}
            onClick={() => {
              window.open(link.creditReportUri, '_blank');
              setIsOpen(false);
            }}
          >
            {link.displayName === 'Credit Report'
              ? link.displayName
              : `${link.displayName} - Credit Report`}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

const TableLayout = ({ data, type }: { data: any[]; type: 'DU' | 'LPA' }) => {
  const { showBothTables } = useAUSStore();
  return (
    <TableContainer
      sx={{
        width: '100%',
        border: `1px solid ${palette.gray[200]}`,
        borderRadius: '4px',
        overflowX: 'hidden',
        display: 'block',
        minWidth: 0,
      }}
    >
      <Table
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          display: 'block',
          minWidth: 0,
        }}
      >
        <Thead
          sx={{
            backgroundColor: palette.gray[100],
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'table',
            width: '100%',
            tableLayout: 'fixed',
            '& th': {
              fontWeight: 400,
              color: palette.gray[700],
              paddingTop: '8px',
              paddingBottom: '8px',
              paddingLeft: 0,
              paddingRight: 0,
            },

            '& th:first-of-type': { paddingLeft: '8px' },
            '& th:last-of-type': {
              paddingRight: '8px',
            },
          }}
        >
          <Tr>
            <Th scope="col" sx={{ width: '14%' }}>
              Date
            </Th>
            <Th scope="col" sx={{ width: '12%' }}>
              Time
            </Th>
            <Th scope="col" sx={{ width: '15%', whiteSpace: 'nowrap' }}>
              Ran by
            </Th>
            <Th scope="col" sx={{ width: '32%' }}>
              Recommendation
            </Th>
            <Th
              scope="col"
              sx={{ width: type === 'DU' || showBothTables ? '20%' : '21%' }}
            >
              {type} Findings / Credit
            </Th>
          </Tr>
        </Thead>
        <Tbody
          sx={{
            display: showBothTables ? 'table' : 'block',
            maxHeight: showBothTables ? 'none' : 424,
            overflowY: showBothTables ? 'visible' : 'auto',
            width: '100%',
            minHeight: 423,
            backgroundColor: palette.gray[100],
          }}
        >
          {data?.length === 0 ? (
            <Tr sx={{ backgroundColor: palette.gray[100], display: 'block' }}>
              <Td
                colSpan={5}
                sx={{
                  textAlign: 'center',
                  py: 4,
                  color: palette.gray[700],
                  height: 423,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                AUS has not been run with {type} (
                {type === 'LPA'
                  ? 'Loan Product Advisor'
                  : 'Desktop Underwriter'}
                )
              </Td>
            </Tr>
          ) : (
            data.map((row) => (
              <Tr
                key={row.requestIdentifier.requestId}
                sx={{
                  display: showBothTables ? 'table' : 'block',
                  tableLayout: 'fixed',
                  width: '100%',
                  backgroundColor: palette.white,
                  '& td': {
                    fontSize: '1rem',
                    color: palette.black,
                    paddingTop: 0,
                    paddingBottom: 0,
                    paddingLeft: 0,
                    paddingRight: 0,
                  },
                  '& td:first-of-type': { paddingLeft: '8px' },
                  '& td:last-of-type': {
                    padding: '6px 8px 6px 8px',
                    border: 'unset',
                  },
                }}
              >
                <Td sx={{ width: '15%', fontWeight: 500 }}>
                  {row.requestShortDate}
                </Td>
                <Td sx={{ width: '13%' }}>{row.requestTime}</Td>
                <Td sx={{ width: '16%' }}>
                  <RanByCell username={row.requestUser} />
                </Td>
                <Td sx={{ width: '34%' }}>
                  <RecommendationCell text={row.recommendation} />
                </Td>
                <Td sx={{ width: '22%' }}>
                  <FindingsCell
                    type={type}
                    requestId={row.requestIdentifier.requestId}
                    requestDateTime={row.requestDate}
                    requestStatus={row.requestStatus}
                    recommendation={row.recommendation}
                  />
                </Td>
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const AUSHistory = () => {
  const { historyType, showBothTables, setShowBothTables } = useAUSStore();
  const {
    obfuscatedLoanIdentifier: {
      obfuscatedLenderDatabaseId,
      obfuscatedLoanRecordId,
    },
  } = useUrl();

  const { data } = useQuery({
    queryKey: [
      'ausRequestHistory',
      obfuscatedLenderDatabaseId,
      obfuscatedLoanRecordId,
    ],
    queryFn: async () => {
      const response = await typedApiClient.v1.ausRequestHistoryList(
        obfuscatedLenderDatabaseId,
        obfuscatedLoanRecordId,
      );
      return response.data;
    },
    enabled: !!obfuscatedLenderDatabaseId && !!obfuscatedLoanRecordId,
  });

  useEffect(() => {
    // Check for showBothTables param in URL
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get('showBothTables') === '1') {
      setShowBothTables(true);
    }
    const handleBeforeUnload = () => {
      setShowBothTables(false);
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [setShowBothTables]);

  return (
    <Box>
      {showBothTables ? (
        // Only show both tables in a popup window
        <Box
          display="flex"
          gap={12}
          sx={{
            margin: '2rem',
            flexDirection: {
              xl: 'row',
              lg: 'column',
              xs: 'column',
              sm: 'column',
            },
            alignItems: 'stretch',
            width: 'calc(100vw - 4rem)',
          }}
        >
          <Box flex={1} sx={{ width: '100%', minWidth: 650 }}>
            <Box component="h2" sx={{ marginBottom: '1.5rem' }}>
              DU - Desktop Underwriter
            </Box>
            <TableLayout data={data?.duRequests ?? []} type="DU" />
          </Box>
          <Box flex={1} sx={{ width: '100%', minWidth: 650 }}>
            <Box component="h2" sx={{ marginBottom: '1.5rem' }}>
              LPA - Loan Product Advisor
            </Box>
            <TableLayout data={data?.lpaRequests ?? []} type="LPA" />
          </Box>
        </Box>
      ) : (
        // Otherwise, show selected table
        <TableLayout
          data={
            historyType === 'DU'
              ? (data?.duRequests ?? [])
              : (data?.lpaRequests ?? [])
          }
          type={historyType}
        />
      )}
    </Box>
  );
};

export default AUSHistory;
